import Notification from '../core/Notification';
import { Table, TableBody, TableHead, TableCell, TableRow, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import ActionText from '../core/ActionText';
import { globalStyles as styles } from '../core/style/gstheme';
import { getGraphFromProject, getProjectFromGraph, makeCancelable, sortResources, sortResourcesByLangPropList } from '../core/utils/utils';
import Warning from '../core/Warning';
import GS from '../grafsync/grafsync';
import CheckingStatus from './CheckingStatus';
import CommonIriDisplay from './CommonIriDisplay';

const BUILTIN_TYPES = [
    "http://www.w3.org/2004/02/skos/core#Concept",
    "http://www.w3.org/2004/02/skos/core#ConceptScheme",
    "http://www.w3.org/2004/02/skos/core#Collection",
    "http://www.w3.org/2004/02/skos/core#OrderedCollection",
    "http://www.w3.org/2008/05/skos-xl#Label",
    "https://grafsync.graphifi.com/schema/gs/Resource",
    "http://www.w3.org/2002/07/owl#NamedIndividual"
];

class TaxonomyValidation extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            queryCount: 0,
            queries: [],
            queriesInfo: [],
            errorsList: [],
            validationResults: [],
            checksSuccessful: undefined,
            errors: false,
            isLoading: true,
            isErrored: false,
            maybeMore: false,
            stopChecks: false,
            resources: [],
            sortedResources: []
        };
        this.cancellable = null;
        this.qRef = React.createRef();
    }

    componentDidMount() {
        this.computeQueries();
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    loadData(iris, graph) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, graph));
        return this.cancellable;
    }

    doRunSparql(graph, query, queryProject) {
        const projectGraph = queryProject ? [queryProject] : [];
        this.cancellable2 = makeCancelable(GS.sparqlProject(graph, query, projectGraph, [400, 500]));
        return this.cancellable2;
    }

    runQueries = async (sortProps) => {
        try {
            const { queriesInfo } = this.state;
            const { graph, defaultLang } = this.props;
            let validationResults = [];
            // Unique list of resources with issues
            let resources = [];
            let queryCount = 0;
            let errors = false;
            let errorsList = [];
            let maybeMore = false;
            for (let queryInfo of queriesInfo) {
                if (this.state.stopChecks) break;
                const resultsData = await this.doRunSparql(graph, queryInfo.query, queryInfo.projectGraph).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                // Status indicates a bad query or timeout error
                if (resultsData.status) {
                    errors = true;
                    errorsList.push(queryInfo.reason + " with expected result - " + queryInfo.expected);
                    // We reset the click idler - we don't want log out kicking in during checking
                    if (this.qRef && this.qRef.current) {
                        this.qRef.current.click();
                    }
                }
                else {
                    if (resultsData && resultsData.results && resultsData.results.bindings && resultsData.results.bindings.length > 0) {
                        const resourceList = resultsData.results.bindings.filter(result => {
                            const resource = result.value.value;
                            return resource.startsWith("http");
                        }).map(result => result.value.value);
                        const resourceSet = [...new Set([...resourceList])];
                        // Load all the value resources
                        await this.loadData(resourceSet, graph).promise;
                        if (this.cancellable && this.cancellable.isCancelled()) return;
                        const linkedProjects = this.props.associatedProject ? [...this.props.associatedProject].sort() : [];
                        // Then we get a list of what is not in this graph
                        // From inComponent testing
                        let doesNotExist = resourceList.filter(iri => !GS.isLoaded(iri, graph));
                        let lpCounter = 0;
                        let graphInfo = {};
                        // Then we search linked projects for these resources
                        while (doesNotExist.length > 0 && lpCounter < linkedProjects.length) {
                            const linkedGraph = getGraphFromProject(linkedProjects[lpCounter]);
                            await this.loadData([...doesNotExist], linkedGraph).promise;
                            if (this.cancellable && this.cancellable.isCancelled()) return;
                            const doesNowExist = doesNotExist.filter(iri => GS.isLoaded(iri, linkedGraph));
                            doesNowExist.forEach(iri => graphInfo[iri] = linkedGraph);
                            doesNotExist = doesNotExist.filter(iri => !GS.isLoaded(iri, linkedGraph));
                            lpCounter++;
                        }
                        resultsData.results.bindings.forEach(result => {
                            const resource = result.resource.value;
                            const value = result.value ? result.value.value : undefined;
                            validationResults.push({
                                resource,
                                ...(value) && { value },
                                label: queryInfo.label,
                                templateGraph: queryInfo.templateGraph,
                                expected: queryInfo.expected,
                                valueGraph: value.startsWith("http") && graphInfo[value] ? graphInfo[value] : graph,
                                projectGraph: queryInfo.projectGraph,
                                path: queryInfo.path,
                                reason: queryInfo.reason,
                                templateName: queryInfo.templateName
                            });
                            if (!resources.includes(resource)) resources.push(resource);
                        });
                        if (resultsData.results.bindings.length >= 100) {
                            maybeMore = true;
                        }
                        await this.loadData(resources, graph).promise;
                    }
                    // We reset the click idler - we don't want log out kicking in during checking
                    if (this.qRef && this.qRef.current) {
                        this.qRef.current.click();
                    }
                }
                // Sort by IRI first to handle concepts/labels with same label
                const sortedByID = sortResources(resources, graph, "id", defaultLang);
                const sortedResources = sortResourcesByLangPropList(sortedByID, graph, sortProps, defaultLang);
                queryCount++;
                this.setState({ validationResults, resources, sortedResources, queryCount, errors, errorsList, maybeMore });
            }
            this.setState({ isLoading: false, checksSuccessful: validationResults.length === 0 });
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({
                isErrored: true,
                isLoading: false,
                message: "Could not run all of the tests for validation."
            });
        }
    };

    shMinCount(graph, basicResourceClause, shPropertyData) {
        let clause = "";
        // Special case for 1 as common so make it efficient
        if (shPropertyData.shMinCount === 1) {
            clause = `SELECT DISTINCT ?resource ?value WHERE {
                GRAPH <${graph}> {
                    ${basicResourceClause}
                    FILTER NOT EXISTS {
                        ?resource <${shPropertyData.shPath}> ?example .
                    }
                    BIND (0 AS ?value)
                }
            }
            LIMIT 100`;
        }
        else {
            // Note this returns 100 values - not resources!
            clause = `SELECT DISTINCT ?resource ?value WHERE {
                {
                    GRAPH <${graph}> {
                        ${basicResourceClause}
                        FILTER NOT EXISTS {
                            ?resource <${shPropertyData.shPath}> ?example .
                        }
                        BIND (0 AS ?value)
                    }
                }
                UNION
                {
                    SELECT DISTINCT ?resource (COUNT(DISTINCT ?example) AS ?value) WHERE {
                        GRAPH <${graph}> {
                            ?resource <${shPropertyData.shPath}> ?example .
                        }
                        GRAPH <${graph}> {
                            ${basicResourceClause}
                        }
                    }
                    GROUP BY ?resource
                    HAVING (?value < ${shPropertyData.shMinCount})
                    LIMIT 100
                }
            }
            LIMIT 100`;
        }
        return clause;
    }

    shMaxCount(graph, basicResourceClause, shPropertyData) {
        let clause = "";
        clause = `SELECT DISTINCT ?resource (COUNT(DISTINCT ?example) AS ?value) WHERE {
            GRAPH <${graph}> {
                ?resource <${shPropertyData.shPath}> ?example .
            }
            GRAPH <${graph}> {
                ${basicResourceClause}
            }
        }
        GROUP BY ?resource
        HAVING (?value > ${shPropertyData.shMaxCount})
        LIMIT 100`;
        return clause;
    }

    shMaxLength(graph, basicResourceClause, shPropertyData) {
        let clause = "";
        clause = `SELECT DISTINCT ?resource ?value WHERE {
            GRAPH <${graph}> {
                ?resource <${shPropertyData.shPath}> ?value .
                FILTER (STRLEN(?value) > ${shPropertyData.shMaxLength})
            }
            GRAPH <${graph}> {
                ${basicResourceClause}
            }
        }
        LIMIT 100`;
        return clause;
    }

    inComponent(graph, basicResourceClause, shPropertyData) {
        let clause = "";
        const targetProjectGraph = shPropertyData.inComponentProject ?
            getGraphFromProject(shPropertyData.inComponentProject)
            :
            graph;
        clause = `SELECT DISTINCT ?resource ?value WHERE {
            GRAPH <${graph}> {
                ?resource <${shPropertyData.shPath}> ?value .
                FILTER NOT EXISTS {
                    GRAPH <${targetProjectGraph}> {
                        ?value (gs:inDataset | skos:inScheme) <${shPropertyData.inComponent}> .
                    }
                }
            }
            GRAPH <${graph}> {
                ${basicResourceClause}
            }
        }
        LIMIT 100`;
        return clause;
    }

    shPattern(graph, basicResourceClause, shPropertyData) {
        let clause = "";
        let text = shPropertyData.shPattern;
        text = text.replace(/\\/g, "\\\\");
        // We assume we need to escape any brace followed by a non-digit
        // And any brace preceded by a non-digit
        // Note we use JS to validaate innput but SPARQL to run validation
        // See: https://www.npmjs.com/package/@comunica/actor-function-factory-term-regex
        text = text.replace(/\{([^0-9])/g, "\\\\{$1");
        text = text.replace(/([^0-9])\}/g, "$1\\\\}");
        text = text.replace(/"/g, "\\u0022");
        clause = `SELECT DISTINCT ?resource ?value WHERE {
            GRAPH <${graph}> {
                ?resource <${shPropertyData.shPath}> ?value .
                FILTER (!REGEX(?value, "${text}"))
                ${basicResourceClause}
            }
        }
        LIMIT 100`;
        return clause;
    }

    getSubclasses = async (associatedClauseTypes, templateGraph) => {
        const subclassQuery = `SELECT ?subclassIRI {
            GRAPH <${templateGraph}> {
                VALUES ?superClassIRI {${associatedClauseTypes.map(ct => `<${ct}>`).join(" ")}}
                ?subclassIRI <http://www.w3.org/2000/01/rdf-schema#subClassOf>+ ?superClassIRI
            }
        }`;
        const resultsData = await this.doRunSparql(templateGraph, subclassQuery).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return [];
        // Status indicates a bad query or timeout error
        if (resultsData.status) throw new Error("Cannot load subclass information.");
        if (resultsData && resultsData.results && resultsData.results.bindings && resultsData.results.bindings.length > 0) {
            return resultsData.results.bindings.map(result => result.subclassIRI.value);
        }
        return [];
    };

    computeQueries = async () => {
        try {
            const { templateInfo, data, graph, resourceTypes, defaultLang, resourceIn } = this.props;
            let queriesInfo = [];
            let domainQueries = {};
            if (!("associatedTemplate" in data) || data.associatedTemplate.length === 0) return;
            for (let at of data.associatedTemplate) {
                const templateInfoData = templateInfo.find(ti => ti.iri === at);
                const templateGraph = templateInfoData.associatedProjectGraph;
                await this.loadData([at], graph).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                const templateData = GS.data(at, graph);
                let clauseTypes = "shTargetClass" in templateData && templateData.shTargetClass.length > 0 ?
                    [...templateData.shTargetClass]
                    :
                    resourceTypes;
                const associatedClauseTypes = clauseTypes.filter(ct => !BUILTIN_TYPES.includes(ct));
                let inferredSubclasses = [];
                if (associatedClauseTypes.length > 0) {
                    // Get all the subclasses of that class
                    inferredSubclasses = await this.getSubclasses(associatedClauseTypes, templateGraph);
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    clauseTypes = clauseTypes.concat(inferredSubclasses);
                }
                if ("shProperty" in templateData) {
                    for (let shProperty of templateData.shProperty) {
                        const shPropertyData = GS.data(shProperty, graph);
                        await this.loadData([shPropertyData.shPath], templateGraph).promise;
                        if (this.cancellable && this.cancellable.isCancelled()) return;
                        const propertyData = GS.data(shPropertyData.shPath, templateGraph);
                        const label = propertyData.label[defaultLang] || shPropertyData.shPath;
                        let clauses = [];
                        const isFunctionalProperty = propertyData.type.includes("FunctionalProperty");
                        const isBoolean = "range" in propertyData && propertyData.range.includes("http://www.w3.org/2001/XMLSchema#boolean");
                        const domains = "domain" in propertyData && propertyData.domain.length > 0 ?
                            propertyData.domain
                            :
                            [];
                        let domainClause = "";
                        if (domains.length > 0) {
                            const domainsKey = domains.sort().join("-");
                            if (domainQueries[domainsKey]) {
                                domainClause = domainQueries[domainsKey];
                            }
                            else {
                                let inferredDomainClasses = await this.getSubclasses(domains, templateGraph);
                                if (this.cancellable && this.cancellable.isCancelled()) return;
                                inferredDomainClasses = inferredDomainClasses.concat(domains);
                                domainClause = `
                                    VALUES ?resourceDomainType {${inferredDomainClasses.map(ct => `<${ct}>`).join(" ")}}
                                    ?resource a ?resourceDomainType`;
                                // Store to avoid recomputing again if it occurs
                                domainQueries[domainsKey] = domainClause;
                            }
                        }
                        if (isBoolean || isFunctionalProperty) {
                            // Okay to do as GS.data returns clone
                            shPropertyData.shMaxCount = 1;
                        }
                        const basicResourceClause = `
                            ${domainClause}
                            VALUES ?resourceType {${clauseTypes.map(ct => `<${ct}>`).join(" ")}}
                            ?resource a ?resourceType .
                            ?resource <${resourceIn}> <${data.id}> .`;
                        if ("shMinCount" in shPropertyData) {
                            let clause = this.shMinCount(graph, basicResourceClause, shPropertyData);
                            clauses.push({
                                clause,
                                expected: `${shPropertyData.shMinCount}`,
                                reason: `Not enough values`
                            });
                        }
                        if ("shMaxCount" in shPropertyData) {
                            let clause = this.shMaxCount(graph, basicResourceClause, shPropertyData);
                            clauses.push({
                                clause,
                                expected: `${shPropertyData.shMaxCount}`,
                                reason: `Too many values`
                            });
                        }
                        if ("shMaxLength" in shPropertyData) {
                            let clause = this.shMaxLength(graph, basicResourceClause, shPropertyData);
                            clauses.push({
                                clause,
                                expected: `Maximum length of ${shPropertyData.shMaxLength}`,
                                reason: `Text is too long`
                            });
                        }
                        if ("shPattern" in shPropertyData) {
                            let clause = this.shPattern(graph, basicResourceClause, shPropertyData);
                            clauses.push({
                                clause,
                                expected: `Pattern matching ${shPropertyData.shPattern}`,
                                reason: `Value does not conform to pattern`
                            });
                        }
                        if ("inComponent" in shPropertyData && shPropertyData.inComponent.length > 0) {
                            let clause = this.inComponent(graph, basicResourceClause, shPropertyData, resourceIn);
                            clauses.push({
                                clause,
                                ...("inComponentProject" in shPropertyData) && {
                                    projectGraph: getGraphFromProject(shPropertyData.inComponentProject)
                                },
                                expected: `${shPropertyData.inComponent}`,
                                reason: `Resource is not from expected component`
                            });
                        }
                        if (clauses.length > 0) {
                            clauses.forEach(c => {
                                const fullQuery = `PREFIX skos: <http://www.w3.org/2004/02/skos/core#>\nPREFIX gs: <https://grafsync.graphifi.com/schema/gs/>\n` +
                                    `${c.clause}`;
                                queriesInfo.push({
                                    query: fullQuery,
                                    templateGraph,
                                    ...(c.projectGraph) && { projectGraph: c.projectGraph },
                                    label,
                                    expected: c.expected,
                                    path: shPropertyData.shPath,
                                    reason: c.reason,
                                    templateName: templateData.label && templateData.label[defaultLang] ?
                                        templateData.label[defaultLang]
                                        :
                                        "Not available"
                                });
                            });
                        }
                    }
                }
            }
            this.setState({ queriesInfo }, () => this.runQueries(["prefLabel", "literalForm", "label"]));
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({
                isErrored: true,
                isLoading: false,
                message: "Could not run all of the tests for validation. Check that you have permissions to all necessary projects."
            });
        }
    };

    onStop = () => this.setState({ stopChecks: true });

    onExitWarning = () => this.setState({ isErrored: false });

    render() {

        const { graph, defaultLang, projectIRI, onToggleView, data, classes } = this.props;
        const { validationResults, sortedResources, isErrored, queriesInfo, maybeMore, errorsList,
            message, checksSuccessful, queryCount, isLoading, errors } = this.state;

        if (isErrored) return <Warning message={message} onExitWarning={this.onExitWarning} />;

        if (!data.associatedTemplate || data.associatedTemplate.length === 0) {
            return <div style={{ marginBottom: 120 }}>
                <ActionText dataTest="no-templates" offset="120px">Nothing to validate as no templates configured.</ActionText>
            </div>;
        }

        const queryItems = queriesInfo.length;
        if (queryItems === 0) {
            return <div style={{ marginBottom: 120 }}>
                <ActionText dataTest="no-applicable-constraints" offset="120px">Nothing to validate as no applicable constraints.</ActionText>
            </div>;
        }

        return (
            <div ref={this.qRef} style={{ margin: '6px', overflowX: 'scroll' }}>
                <CheckingStatus
                    onStop={this.onStop}
                    progressCount={queryCount}
                    checksInconclusive={errors}
                    checksSuccessful={checksSuccessful}
                    itemCount={queryItems} />
                {errors &&
                    <div style={{ marginTop: 6, marginBottom: 6 }}>
                        <Notification dataTest="errors-happened" text={"Some checks failed to run: " + errorsList.join(", ")} />
                    </div>
                }
                {!isLoading && checksSuccessful && !errors &&
                    <div style={{ height: '120px' }}>
                        <ActionText dataTest="everything-good" offset="120px">Everything looks good. There is nothing to do.</ActionText>
                    </div>
                }
                {sortedResources.length > 0 &&
                    <React.Fragment>
                        <Typography className={classes.mainHeading} style={{ marginTop: '20px', marginLeft: '6px' }}>Validation Failures</Typography>
                        {maybeMore &&
                            <Typography
                                data-test="maybe-more"
                                variant="body2"
                                color="textPrimary"
                                style={{ marginLeft: '6px' }}>Note that there may be more failures. Only a limited amount of errors are displayed.</Typography>
                        }
                        <Table data-test="validation-failures">
                            <TableHead>
                                <TableRow data-test="headers">
                                    <TableCell data-test="label-title" component="th" className={classes.tableHeading}>Label</TableCell>
                                    <TableCell component="th" className={classes.tableHeading} style={{ paddingLeft: '0px' }}>Property</TableCell>
                                    <TableCell component="th" className={classes.tableHeading} style={{ paddingLeft: '0px' }}>Issue</TableCell>
                                    <TableCell component="th" className={classes.tableHeading} style={{ paddingLeft: '0px' }}>Expected</TableCell>
                                    <TableCell component="th" className={classes.tableHeading} style={{ paddingLeft: '0px' }}>Value/Count</TableCell>
                                    <TableCell component="th" className={classes.tableHeading} style={{ paddingLeft: '0px' }}>Template</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedResources.map((resource, rIndex) => {
                                    const filteredResults = validationResults
                                        .filter(vr => vr.resource === resource)
                                        .sort((a, b) => a.label < b.label ?
                                            -1
                                            :
                                            a.label > b.label ?
                                                1
                                                :
                                                a.reason + "-" + a.expected + "-" + a.value < b.reason + "-" + b.expected + "-" + b.value ?
                                                    -1
                                                    :
                                                    1
                                        );
                                    return filteredResults.map((vr, vrIndex) => {
                                        const includeBorder = vrIndex === filteredResults.length - 1;
                                        return <TableRow
                                            key={resource + "-" + vrIndex}
                                            data-test={"validation-row-" + rIndex + "-" + vrIndex}
                                            style={{
                                                backgroundColor: rIndex % 2 === 0 ? '#eeeeee' : 'transparent'
                                            }}
                                        >
                                            {vrIndex === 0 ?
                                                <TableCell
                                                    className={classes.tableCellText}
                                                    style={{
                                                        paddingTop: '8px',
                                                        paddingBottom: '8px',
                                                        paddingLeft: '6px',
                                                        ...(!includeBorder) && { borderBottom: 'none' }
                                                    }}>
                                                    <CommonIriDisplay
                                                        dataTest="resource"
                                                        id={resource}
                                                        loadComponents={true}
                                                        superCompact={true}
                                                        handleNotAvailable={true}
                                                        graph={graph}
                                                        projectIRI={projectIRI}
                                                        onLink={onToggleView}
                                                        wrap={false}
                                                        isReadOnly={true}
                                                        defaultLang={defaultLang}
                                                        viewLang={defaultLang}
                                                    />
                                                </TableCell>
                                                :
                                                <TableCell
                                                    className={classes.tableCellText}
                                                    style={{
                                                        paddingTop: '8px',
                                                        paddingBottom: '8px',
                                                        paddingLeft: '6px',
                                                        ...(!includeBorder) && { borderBottom: 'none' }
                                                    }}>
                                                </TableCell>
                                            }
                                            <TableCell
                                                className={classes.tableCellText}
                                                style={{
                                                    paddingTop: '8px',
                                                    paddingBottom: '8px',
                                                    paddingLeft: '0px',
                                                    ...(!includeBorder) && { borderBottom: 'none' }
                                                }}>
                                                <CommonIriDisplay
                                                    dataTest="property"
                                                    id={vr.path}
                                                    key={vr.path}
                                                    superCompact={true}
                                                    handleNotAvailable={true}
                                                    loadComponents={true}
                                                    graph={vr.templateGraph}
                                                    projectIRI={getProjectFromGraph(vr.templateGraph)}
                                                    onLink={onToggleView}
                                                    wrap={false}
                                                    isReadOnly={true}
                                                    defaultLang={defaultLang}
                                                    viewLang={defaultLang}
                                                />
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableCellText}
                                                style={{
                                                    paddingTop: '8px',
                                                    paddingBottom: '8px',
                                                    paddingLeft: '0px',
                                                    ...(!includeBorder) && { borderBottom: 'none' }
                                                }}>
                                                <Typography data-test="reason" variant="body2" color="textPrimary">
                                                    {vr.reason}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableCellText}
                                                style={{
                                                    paddingTop: '8px',
                                                    paddingBottom: '8px',
                                                    paddingLeft: '0px',
                                                    ...(!includeBorder) && { borderBottom: 'none' }
                                                }}>
                                                {vr.expected.startsWith('http') ?
                                                    <CommonIriDisplay
                                                        dataTest="expected"
                                                        id={vr.expected}
                                                        key={vr.expected}
                                                        superCompact={true}
                                                        loadComponents={true}
                                                        handleNotAvailable={true}
                                                        graph={vr.projectGraph}
                                                        highlightNotAvailable={true}
                                                        projectIRI={getProjectFromGraph(vr.projectGraph)}
                                                        onLink={onToggleView}
                                                        wrap={false}
                                                        isReadOnly={true}
                                                        defaultLang={defaultLang}
                                                        viewLang={defaultLang}
                                                    />
                                                    :
                                                    <Typography data-test="expected" variant="body2" color="textPrimary">
                                                        {vr.expected}
                                                    </Typography>
                                                }
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableCellText}
                                                style={{
                                                    paddingTop: '8px',
                                                    paddingBottom: '8px',
                                                    paddingLeft: '0px',
                                                    ...(!includeBorder) && { borderBottom: 'none' }
                                                }}>
                                                {vr.value && vr.value.startsWith('http') ?
                                                    <CommonIriDisplay
                                                        dataTest="actual"
                                                        id={vr.value}
                                                        key={vr.value}
                                                        handleNotAvailable={true}
                                                        superCompact={true}
                                                        loadComponents={true}
                                                        sourceGraph={graph}
                                                        graph={vr.valueGraph}
                                                        highlightNotAvailable={true}
                                                        projectIRI={getProjectFromGraph(vr.valueGraph)}
                                                        onLink={onToggleView}
                                                        wrap={false}
                                                        isReadOnly={true}
                                                        defaultLang={defaultLang}
                                                        viewLang={defaultLang}
                                                    />
                                                    :
                                                    vr.value ?
                                                        <Typography data-test="actual" variant="body2" color="textPrimary">
                                                            {vr.value}
                                                        </Typography>
                                                        :
                                                        null
                                                }
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableCellText}
                                                style={{
                                                    paddingTop: '8px',
                                                    paddingBottom: '8px',
                                                    paddingLeft: '0px',
                                                    ...(!includeBorder) && { borderBottom: 'none' }
                                                }}>
                                                <Typography data-test="template" variant="body2" color="textPrimary">
                                                    {vr.templateName}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>;
                                    });
                                })}
                            </TableBody>
                        </Table>
                    </React.Fragment>
                }
            </div>
        );
    }

}

TaxonomyValidation.propTypes = {
    onToggleView: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    resourceIn: PropTypes.string.isRequired,
    resourceTypes: PropTypes.array.isRequired,
    data: PropTypes.object.isRequired,
    skosXL: PropTypes.bool,
    projectIRI: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    ontologyProjectAssociation: PropTypes.array,
    graph: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(withRouter(TaxonomyValidation));